import React from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Star, 
  Heart, 
  Brain, 
  DollarSign, 
  Users, 
  GraduationCap, 
  Briefcase,
  RotateCcw,
  Share2,
  Download
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';

const GameComplete: React.FC = () => {
  const { 
    stats, 
    currentAge, 
    completedDecisions, 
    timeline, 
    score,
    resetGame 
  } = useGameStore();

  const getStatIcon = (stat: string) => {
    const icons = {
      health: Heart,
      happiness: Brain,
      wealth: DollarSign,
      relationships: Users,
      education: GraduationCap,
      career: Briefcase,
    };
    return icons[stat as keyof typeof icons] || Heart;
  };

  const getStatColor = (value: number) => {
    if (value >= 80) return 'text-success-600';
    if (value >= 60) return 'text-warning-600';
    return 'text-danger-600';
  };

  const getStatBgColor = (value: number) => {
    if (value >= 80) return 'bg-success-50 border-success-200';
    if (value >= 60) return 'bg-warning-50 border-warning-200';
    return 'bg-danger-50 border-danger-200';
  };

  const getLifeRating = () => {
    const averageScore = Object.values(stats).reduce((a, b) => a + b, 0) / Object.keys(stats).length;
    if (averageScore >= 90) return { rating: 'Exceptional', color: 'text-yellow-600', icon: '🌟' };
    if (averageScore >= 80) return { rating: 'Excellent', color: 'text-green-600', icon: '⭐' };
    if (averageScore >= 70) return { rating: 'Good', color: 'text-blue-600', icon: '👍' };
    if (averageScore >= 60) return { rating: 'Fair', color: 'text-orange-600', icon: '👌' };
    return { rating: 'Challenging', color: 'text-red-600', icon: '💪' };
  };

  const lifeRating = getLifeRating();

  const handleShare = () => {
    const shareText = `I just completed SppefRun Life! 🏃\n\nAge: ${currentAge}\nDecisions Made: ${completedDecisions.length}\nLife Rating: ${lifeRating.rating}\nScore: ${score}\n\nTry it yourself!`;
    
    if (navigator.share) {
      navigator.share({
        title: 'SppefRun Life - Game Complete',
        text: shareText,
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Results copied to clipboard!');
    }
  };

  const handleExportResults = () => {
    const results = {
      finalAge: currentAge,
      finalStats: stats,
      decisionsMade: completedDecisions.length,
      timelineEvents: timeline.length,
      finalScore: score,
      lifeRating: lifeRating.rating,
      completedAt: new Date().toISOString(),
    };

    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `sppefrun-life-results-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Main Completion Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-strong border border-white/20 mb-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-strong"
            >
              <Trophy size={48} className="text-white" />
            </motion.div>
            
            <h1 className="text-5xl font-bold text-slate-800 mb-4">
              Life Journey Complete! 🎉
            </h1>
            
            <div className="flex items-center justify-center space-x-4 mb-6">
              <span className="text-2xl">{lifeRating.icon}</span>
              <span className={`text-3xl font-bold ${lifeRating.color}`}>
                {lifeRating.rating} Life
              </span>
            </div>
            
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              You've navigated through {currentAge} years of life, making {completedDecisions.length} important decisions. 
              Your journey has been {lifeRating.rating.toLowerCase()}.
            </p>
          </div>

          {/* Final Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {Object.entries(stats).map(([key, value]) => {
              const Icon = getStatIcon(key);
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + Object.keys(stats).indexOf(key) * 0.1 }}
                  className={`p-6 rounded-2xl border-2 ${getStatBgColor(value)}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon size={24} className={getStatColor(value)} />
                    <span className={`text-3xl font-bold ${getStatColor(value)}`}>
                      {value}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 capitalize mb-2">
                    {key}
                  </h3>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${
                        value >= 80 ? 'bg-success-500' : 
                        value >= 60 ? 'bg-warning-500' : 
                        'bg-danger-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${value}%` }}
                      transition={{ duration: 1, ease: 'easeOut', delay: 0.8 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Game Summary */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-6 mb-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-4 text-center">Game Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">{currentAge}</div>
                <div className="text-slate-600">Years Lived</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-success-600 mb-2">{completedDecisions.length}</div>
                <div className="text-slate-600">Decisions Made</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-warning-600 mb-2">{timeline.length}</div>
                <div className="text-slate-600">Life Events</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">{score}</div>
                <div className="text-slate-600">Final Score</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleShare}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors"
            >
              <Share2 size={20} />
              <span>Share Results</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExportResults}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-slate-500 hover:bg-slate-600 text-white font-semibold rounded-xl transition-colors"
            >
              <Download size={20} />
              <span>Export Results</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (window.confirm('Are you sure you want to start a new game? This will reset all your progress.')) {
                  resetGame();
                }
              }}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-xl border border-slate-300 transition-colors"
            >
              <RotateCcw size={20} />
              <span>New Game</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Reflection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <h3 className="text-2xl font-bold text-slate-800 mb-4">Life Reflection</h3>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
            Every decision you made in this simulation reflects the complexity of real life. 
            In reality, we face similar choices every day that shape our health, relationships, 
            career, and happiness. This game was designed to help you think about the long-term 
            consequences of your decisions and the importance of balance in life.
          </p>
          <div className="mt-6 text-slate-500">
            <p>Thank you for playing SppefRun Life! 🏃‍♂️</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GameComplete;
